import { AppBar, Divider, IconButton, ListItemText, Menu, MenuItem, Stack, Toolbar } from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { type MouseEvent as ReactMouseEvent, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { com } from '@eidu/form'
import AuthenticationContext from '../../io/api/authorization/AuthenticationContext'
import { EntityTypesContext } from '../../io/context/EntityTypes'
import useEntityRepository from '../../io/useEntityRepository'
import EntityWithLabelAndRelated from '../../domain/entity/EntityWithLabelAndRelated'
import { Theme, getThemeConfig } from '../../ui/theme/themeConfig'
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import entityIdFromString = com.eidu.sharedlib.entity.entityIdFromString

const DefaultLogo = () => (
  <img src={getThemeConfig('default').logoSrc} className="appBarIcon" alt="" style={{ height: 24 }} />
)

const KenyaMoeLogo = () => <img src={getThemeConfig('kenyaMoe').logoSrc} className="appBarIcon" alt="" />

const AppBarLogo = ({ theme }: { theme: Theme }) => (theme === 'kenyaMoe' ? <KenyaMoeLogo /> : <DefaultLogo />)

export type TopAppBarProps = {
  authContext: AuthenticationContext | undefined
  signOut?: () => void
  theme?: Theme
  showNavigationMenuButton?: boolean
  onNavigationMenuClick?: () => void
}

const TopAppBar = ({
  authContext,
  signOut,
  theme = 'default',
  showNavigationMenuButton = false,
  onNavigationMenuClick,
}: TopAppBarProps) => {
  const navigate = useNavigate()
  const entityTypes = useContext(EntityTypesContext).useAll()
  const entityRepository = useEntityRepository(
    { typeId: undefined, types: entityTypes ?? new Map<EntityTypeId, EntityType>() },
    [entityTypes]
  )

  const [userEntity, setUserEntity] = useState<EntityWithLabelAndRelated>()
  const [actionsAnchor, setActionsAnchor] = useState<null | HTMLElement>(null)

  useEffect(() => {
    if (authContext)
      entityRepository.get(entityIdFromString(authContext?.userEntityId)).then((entity) => {
        if (entity !== null) setUserEntity(entity)
      })
  }, [authContext, entityRepository])

  const closeActionsMenu = () => {
    setActionsAnchor(null)
  }

  const openActionsMenu = (event: ReactMouseEvent<HTMLElement>) => {
    setActionsAnchor(event.currentTarget)
  }

  const openCurrentUser = () => {
    if (!userEntity) return
    closeActionsMenu()
    navigate(`/entities/${userEntity.entity.typeId.toString()}/view/${userEntity.entity.id.toString()}`)
  }

  const hasActionsMenu = Boolean(userEntity) || Boolean(signOut)
  const actionsMenu = hasActionsMenu ? (
    <>
      <IconButton edge="end" color="inherit" aria-label="Open user actions menu" onClick={openActionsMenu}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={actionsAnchor} open={Boolean(actionsAnchor)} onClose={closeActionsMenu}>
        {userEntity && (
          <MenuItem onClick={openCurrentUser}>
            <ListItemText
              primary="Your Profile"
              secondary={userEntity.primaryLabelText || userEntity.entity.id.toString()}
            />
          </MenuItem>
        )}
        {userEntity && signOut && <Divider />}
        {signOut && (
          <MenuItem
            onClick={() => {
              closeActionsMenu()
              signOut()
            }}
          >
            Log out
          </MenuItem>
        )}
      </Menu>
    </>
  ) : null

  return (
    <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
      <Toolbar>
        {showNavigationMenuButton ? (
          <Stack
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto 1fr',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Stack direction="row" sx={{ alignItems: 'center', justifySelf: 'start' }}>
              <IconButton
                color="inherit"
                edge="start"
                aria-label="Open navigation menu"
                onClick={onNavigationMenuClick}
              >
                <MenuIcon />
              </IconButton>
            </Stack>
            <Stack direction="row" sx={{ alignItems: 'center', justifySelf: 'center' }}>
              <AppBarLogo theme={theme} />
            </Stack>
            <Stack direction="row" sx={{ alignItems: 'center', justifySelf: 'end' }}>
              {actionsMenu}
            </Stack>
          </Stack>
        ) : (
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'left' }}>
              <AppBarLogo theme={theme} />
            </Stack>
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'right' }}>
              {actionsMenu}
            </Stack>
          </Stack>
        )}
      </Toolbar>
    </AppBar>
  )
}

export default TopAppBar
